import { Button, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import { MOCK_BROADCASTS, MOCK_BROADCAST_MESSAGES } from '../mocks/broadcast.ts'
import { ChatConversation, ChatRow } from './chat-shared.tsx'
import './channels.css'

/**
 * Canais de avisos — o modulo "Canais" do rail.
 *
 * No protótipo esses canais sao somente leitura e **apenas administradores
 * publicam**: o botao "+" do explorer nao abre gaveta nenhuma, so avisa
 * (`docs/design-slices/_shell.html:2974`). Os chips sao "Seguindo" e
 * "Explorar" (`_shell.html:2288`), e a barra de somente leitura traz o botao
 * Silenciar (`showChat.html:59`).
 *
 * Dados mocados: seguir/deixar de seguir vive so no estado local.
 */
export function BroadcastPage() {
  const navigate = useNavigate()
  const { id: selectedId = null } = useParams()
  const toast = useToast()
  const { search, chip } = useExplorer()

  const [loading, setLoading] = useState(true)
  // Ids cujo "seguindo" foi invertido nesta sessao.
  const [invertidos, setInvertidos] = useState<readonly string[]>([])
  const [silenciados, setSilenciados] = useState<readonly string[]>([])
  const [lidos, setLidos] = useState<readonly string[]>([])
  const [separadores, setSeparadores] = useState<Record<string, string>>({})

  const seguindo = (id: string, padrao: boolean) => (invertidos.includes(id) ? !padrao : padrao)

  const channels = useMemo(
    () => MOCK_BROADCASTS.map((canal) => ({ ...canal, following: seguindo(canal.id, canal.following) })),
    [invertidos],
  )
  const selected = channels.find((c) => c.id === selectedId) ?? null

  const messages = selected ? (MOCK_BROADCAST_MESSAGES[selected.id] ?? []) : []

  const naoLidasDe = (id: string, unread: number) => (lidos.includes(id) ? 0 : unread)

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 320)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (selectedId === null || lidos.includes(selectedId)) return
    const primeira = (MOCK_BROADCAST_MESSAGES[selectedId] ?? []).find((m) => m.kind === 'message' && !m.read)
    if (primeira) setSeparadores((atual) => ({ ...atual, [selectedId]: primeira.id }))
    setLidos((atual) => [...atual, selectedId])
  }, [selectedId, lidos])

  // chips do modulo `broadcast` em `shell/modules.ts`: Seguindo / Explorar
  const explorando = chip === 'Explorar'
  const rows = channels.filter((canal) => {
    if (canal.following === explorando) return false
    const busca = search.trim().toLowerCase()
    if (busca === '') return true
    return canal.name.toLowerCase().includes(busca) || canal.description.toLowerCase().includes(busca)
  })

  useExplorerConfig({
    footer: loading
      ? 'Carregando canais…'
      : explorando
        ? `${rows.length} ${rows.length === 1 ? 'canal disponível' : 'canais disponíveis'} para seguir`
        : `${rows.length} ${rows.length === 1 ? 'canal seguido' : 'canais seguidos'} · ${channels.reduce(
            (total, canal) => total + (canal.following ? naoLidasDe(canal.id, canal.unread) : 0),
            0,
          )} não lidas`,
    // Protótipo `_shell.html:2974`: criar canal de avisos e restrito.
    onCreate: () => toast.push({ tone: 'info', message: 'Apenas administradores podem criar canais de avisos.' }),
  })

  const alternarSeguir = (id: string, agoraSegue: boolean, nome: string) => {
    setInvertidos((atual) => (atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]))
    toast.push({
      tone: 'success',
      message: agoraSegue ? `Você deixou de seguir “${nome}”.` : `Agora você segue “${nome}”.`,
    })
  }

  return (
    <>
      <ExplorerBody>
        {loading ? (
          <div style={{ padding: 14 }}>
            <LoadingState rows={4} label="Carregando canais" />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '28px 16px' }}>
            <EmptyState
              icon="fas fa-broadcast-tower"
              title={explorando ? 'Nenhum canal para descobrir' : 'Você ainda não segue nenhum canal'}
              description={
                explorando
                  ? 'Todos os canais disponíveis já estão sendo seguidos, ou nenhum combina com a busca.'
                  : 'Use o filtro “Explorar” no topo do painel para encontrar canais de avisos da empresa.'
              }
            />
          </div>
        ) : (
          rows.map((canal) => (
            <ChatRow
              key={canal.id}
              channel={canal}
              selected={canal.id === selectedId}
              unread={naoLidasDe(canal.id, canal.unread)}
              onClick={() => navigate(`/broadcast/${canal.id}`)}
              trailing={
                explorando ? (
                  <button
                    type="button"
                    className="ch-row__follow"
                    aria-label={`Seguir ${canal.name}`}
                    onClick={() => alternarSeguir(canal.id, canal.following, canal.name)}
                  >
                    <i className="fas fa-plus" aria-hidden style={{ fontSize: 9 }} />
                    Seguir
                  </button>
                ) : undefined
              }
            />
          ))
        )}
      </ExplorerBody>

      {selected === null ? (
        <div className="ch-blank">
          <EmptyState
            icon="fas fa-broadcast-tower"
            title="Selecione um canal de avisos"
            description="Canais de avisos são somente leitura: apenas administradores publicam. Escolha um canal no painel para ler os comunicados."
          />
        </div>
      ) : (
        <ChatConversation
          channel={selected}
          messages={messages}
          dividerId={separadores[selected.id] ?? null}
          draft=""
          onDraftChange={() => {}}
          onSend={() => {}}
          subtitle={`${selected.followerCount} seguidores · publicado por ${selected.publisher}`}
          readOnlyLabel={
            silenciados.includes(selected.id)
              ? 'Canal silenciado · apenas administradores publicam aqui'
              : 'Apenas administradores publicam neste canal'
          }
          onMute={() => {
            const jaSilenciado = silenciados.includes(selected.id)
            setSilenciados((atual) =>
              jaSilenciado ? atual.filter((i) => i !== selected.id) : [...atual, selected.id],
            )
            toast.push({
              tone: 'success',
              message: jaSilenciado
                ? `Avisos de “${selected.name}” voltaram a notificar.`
                : `“${selected.name}” silenciado — você continua vendo os avisos aqui.`,
            })
          }}
          readOnlyExtra={
            <Button
              variant="secondary"
              icon={selected.following ? 'fas fa-check' : 'fas fa-plus'}
              onClick={() => alternarSeguir(selected.id, selected.following, selected.name)}
            >
              {selected.following ? 'Seguindo' : 'Seguir canal'}
            </Button>
          }
        />
      )}
    </>
  )
}
